'use client';

/**
 * Drop-in replacement for `next/image` used by news cards.
 *
 * Ported from kannada-prabha's `src/components/ui/CardImage.tsx`. It keeps
 * next/image underneath — so the custom `image-loader.ts`, `deviceSizes`,
 * srcset generation and AVIF/WebP negotiation are all unchanged — and adds the
 * two behaviours dinamani was missing:
 *
 *  1. JS LAZY LOADING. The browser's native `loading="lazy"` threshold is very
 *     generous (it starts fetching roughly a screenful-and-a-half ahead, more on
 *     fast connections). On the home page that meant 24 images downloading for
 *     12 actually in the viewport. A `<img>` that isn't near the viewport yet
 *     renders a transparent pixel instead of its real `src`; an
 *     IntersectionObserver swaps the real one in at `LAZY_ROOT_MARGIN`.
 *
 *  2. A SKELETON. Until the real pixels arrive the box shows a soft pulsing
 *     placeholder instead of empty white. This is what stops the "cards flicker
 *     in one by one" effect on load — the layout is complete and stable from
 *     first paint, and each card fills in place rather than appearing from
 *     nothing.
 *
 * The LCP hero is exempt from both: a `priority` image keeps its real `src` in
 * the SSR HTML and loads eagerly at high fetch priority, so it never waits for
 * hydration. `RowRenderer` grants `priority` to exactly one image per page
 * (first row, widest column, first component, first item), which is why
 * deferring everything else is safe here.
 *
 * TRADE-OFF: with JS disabled, non-priority images stay as skeletons. That
 * matches kannada-prabha's behaviour and is the cost of keeping the initial
 * image count near what's actually on screen.
 */

import NextImage, { type ImageProps } from 'next/image';
import { useEffect, useRef, useState } from 'react';


/**
 * How far ahead of the viewport a card starts loading. Small enough to keep the
 * initial image count close to what's on screen, large enough that normal
 * scrolling doesn't reveal empty boxes.
 */
const LAZY_ROOT_MARGIN = '300px 0px';

/**
 * A 1x1 transparent GIF, used as the `src` of a not-yet-visible card. A data URI
 * costs no network request, and unlike an empty `src` it doesn't make the
 * browser paint its broken-image icon. `image-loader.ts` returns data URIs
 * untouched, so next/image passes it straight through.
 */
const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/** Pulsing skeleton, also hiding the `alt` text, until the real pixels land. */
const SKELETON_CLASS = 'animate-pulse bg-[#eef0f2] dark:bg-[#2a2a2a] text-transparent';

export default function CardImage({
  priority = false,
  className,
  onLoad,
  onError,
  ...rest
}: ImageProps) {
  // Defensive default only. A `fill` image with no `sizes` falls back to
  // next/image's 100vw, which would make a 243px thumbnail request a
  // full-viewport-width candidate. In practice every card component here passes
  // its own `sizes`, so this almost never applies — but a new card that forgets
  // one shouldn't silently pull a 1080px image into a 243px box.
  //
  // `sizes="auto"` asks the browser to use the element's REAL laid-out width. It
  // is only valid on a lazily-loaded image, which is what every non-priority
  // card is; the eager priority hero keeps whatever its caller set. Browsers
  // without support fall back to the previous behaviour.
  const resolvedSizes = rest.sizes ?? (priority ? undefined : 'auto');
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);
  // `visible` gates whether the REAL src is emitted. ONLY the single priority
  // hero skips the observer and renders its src into the SSR HTML.
  //
  // An earlier attempt also exempted the whole 3-row "priority band"
  // (AboveFoldContext) to help desktop Speed Index. It REGRESSED mobile LCP
  // 4.2s -> 4.8s: on a 412px viewport those 3 rows stack vertically, so 25
  // images got a real src and the browser's native lazy heuristic began
  // fetching far-below-fold ones (zodiac icons, market cards) that competed
  // with the hero for Slow-4G bandwidth. kannada-prabha grants priority to
  // exactly one image and defers everything else — matching that here.
  const [visible, setVisible] = useState(priority);

  // Reveal the real src once the element scrolls within LAZY_ROOT_MARGIN.
  // Runs after hydration. Where IntersectionObserver is unavailable, fall back
  // to loading immediately rather than leaving a permanent skeleton.
  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: LAZY_ROOT_MARGIN },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  // A cached image can finish decoding before React attaches `onLoad` (common
  // for the priority hero on hydration), which would leave the skeleton pulsing
  // forever. Clear it from the element's own `complete` flag — but only once the
  // REAL src is in place, since the transparent placeholder also reports
  // `complete` and would wipe the skeleton before any real pixels exist.
  useEffect(() => {
    if (loaded || !visible) return;
    const el = ref.current;
    if (el?.complete && el.naturalWidth > 0) setLoaded(true);
  }, [loaded, visible]);

  const showSkeleton = !loaded;

  return (
    <NextImage
      {...rest}
      ref={ref}
      // Withhold the real src until the card is near the viewport. `unoptimized`
      // on the placeholder stops next/image building a srcset of identical data
      // URIs for it.
      src={visible ? rest.src : TRANSPARENT_PIXEL}
      sizes={resolvedSizes}
      unoptimized={visible ? rest.unoptimized : true}
      className={showSkeleton ? [className, SKELETON_CLASS].filter(Boolean).join(' ') : className}
      priority={priority}
      loading={priority ? 'eager' : 'lazy'}
      onLoad={(event) => {
        // Ignore the placeholder's own load — only the real image clears the
        // skeleton.
        if (!visible) return;
        setLoaded(true);
        onLoad?.(event);
      }}
      onError={(event) => {
        if (!visible) return;
        setLoaded(true);
        onError?.(event);
      }}
    />
  );
}
